'use client';

import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Input,
  Separator,
  Text,
  Flex,
} from '@/_components';
import {
  useContext,
  useEffect,
  useState,
  type FC,
  type FormEvent,
} from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import {
  getNDaysAfter,
  getInputFormattedDateForNDaysFromNow,
  getIsPastDate,
} from '@/_utils/dates';
import { type UploadLaterDialogProps } from './UploadLaterDialog.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';

const TODAYS_DATE: string = getNDaysAfter(0);

export const UploadLaterDialog: FC<UploadLaterDialogProps> = ({
  hasDocuments,
  isOpen,
  isDisabled,
  onCloseDialog,
  onSuccess,
  question,
  mode,
  shouldWaitForQuestionToBeAnswered,
}) => {
  const { selectedQuestionnaire, updateQuestion } = useContext(
    ClientQuestionnaireContext,
  );
  const { updatePreviewQuestion } = useContext(PreviewContext);
  const { updateTemplateQuestion } = useContext(UserPreviewContext);
  const { addToast } = useContext(ToastContext);

  const [answerByDate, setAnswerByDate] = useState(
    getInputFormattedDateForNDaysFromNow(7),
  );
  const [answerByDateError, setAnswerByDateError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (answerByDateError) {
      addToast('error', answerByDateError);
    }
  }, [addToast, answerByDateError]);

  const isBeforeToday: boolean = !getIsPastDate(TODAYS_DATE, answerByDate);

  const validateUploadByDate = () => {
    let error = '';
    const answerByDateAsDate = new Date(answerByDate);

    if (answerByDateAsDate < new Date()) {
      error = 'Upload later date must be in the future';
    }

    if (answerByDateAsDate.getFullYear() !== new Date().getFullYear()) {
      error = 'Upload later date must be in the current year';
    }

    const due_date = selectedQuestionnaire?.due_date || '';

    if (answerByDateAsDate > new Date(due_date)) {
      error = `Upload later date must be before the due date: ${due_date}`;
    }

    setAnswerByDateError(error);
    return error;
  };

  const onUploadLater = async (event: FormEvent) => {
    event.preventDefault();
    const error = validateUploadByDate();

    if (error) return;

    const answerParams = {
      questionId: question?.id!,
      answer: '',
      answerBy: answerByDate,
    };

    try {
      setIsLoading(true);

      const _getMethod = (): Function => {
        if (mode === 'admin-preview') return updatePreviewQuestion;
        if (mode === 'admin-template') return updateTemplateQuestion;

        return updateQuestion;
      };

      const method = _getMethod();

      if (shouldWaitForQuestionToBeAnswered) {
        await method(answerParams);
      } else {
        method(answerParams);
      }

      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onCloseDialog();
      setIsLoading(false);
    }
  };

  const onAnswerByChange = (event: any) => {
    setAnswerByDate(event.target.value);
  };

  return (
    <Dialog isOpen={isOpen} title="Upload Later" onClose={onCloseDialog}>
      <Dialog.Content>
        <form onSubmit={onUploadLater}>
          <Flex isColumn={true} gap={10}>
            {hasDocuments && (
              <Alert type="warning">
                On ”Save”, all documents uploaded on this item will be removed.
              </Alert>
            )}
            <Text>
              Please select the date you can provide this document, so we can
              send you a reminder. You will not be able to complete the
              questionnaire until this has been answered.
            </Text>
            <Input
              label="Upload Later Date"
              isLabelHidden={true}
              type="date"
              min={TODAYS_DATE}
              isDisabled={isDisabled}
              value={answerByDate}
              onChange={onAnswerByChange}
            />
            <Separator isLight={true} />
            <ButtonGroup align="center">
              <Button
                isDisabled={isLoading || isDisabled || isBeforeToday}
                intent="primary"
                value="Save"
                type="submit"
              />
            </ButtonGroup>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};
